import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { FileSpreadsheet, FileDown } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

// ---------------------------------------------------------------
// Constants shared across report tabs
// ---------------------------------------------------------------
export const TAB_LABEL = {
  summary: 'Ringkasan', invoices: 'Invoice', payments: 'Pembayaran',
  receivables: 'Piutang', overdue: 'Overdue', paid: 'Lunas',
  tax: 'Pajak', audit: 'Audit Data',
};

// ---------------------------------------------------------------
// KPI card grid component for summary metrics
// ---------------------------------------------------------------
export function KpiGrid({ items, loading, columns = 4 }) {
  const cls = `grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-${columns} gap-3`;
  return (
    <div className={cls} data-testid="report-kpi-grid">
      {items.map((c) => <KpiItem key={c.label} {...c} loading={loading} />)}
    </div>
  );
}

function KpiItem({ label, value, sub, accent = '', loading }) {
  const accentMap = {
    success: 'border-emerald-400/30 bg-emerald-400/5',
    warn: 'border-amber-400/30 bg-amber-400/5',
    danger: 'border-red-400/30 bg-red-400/5',
    primary: 'border-primary/30 bg-primary/5',
    '': 'border-border bg-card/60',
  };
  const accentValueColor = {
    success: 'text-emerald-300',
    warn: 'text-amber-200',
    danger: 'text-red-300',
    primary: 'text-primary',
    '': 'text-slate-50',
  };
  return (
    <Card className={`border ${accentMap[accent] || accentMap['']}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="text-[11px] text-slate-300 font-medium leading-tight mb-1.5">{label}</div>
        {loading ? (
          <Skeleton className="h-6 w-20" />
        ) : (
          <div className={`font-mono-num text-lg sm:text-xl font-semibold tracking-tight ${accentValueColor[accent] || accentValueColor['']}`}>
            {value}
          </div>
        )}
        {sub ? <div className="text-[10px] text-slate-400 mt-1 truncate">{sub}</div> : null}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------
export function ReportEmpty({ children }) {
  return <div className="text-center py-10 text-sm text-slate-400">{children}</div>;
}

// ---------------------------------------------------------------
// Section header (subtitle) inside a tab
// ---------------------------------------------------------------
export function SectionHeader({ icon: Icon, title, action }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-heading text-base sm:text-lg font-semibold text-slate-100 flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4 text-primary" /> : null}
        {title}
      </h3>
      {action || null}
    </div>
  );
}

// ---------------------------------------------------------------
// Export button group (Excel + optional PDF)
// ---------------------------------------------------------------
export function ExportButtons({ tab, filters, hasPdf = false, disabled = false }) {
  const handleDownload = async (format) => {
    try {
      const params = { ...filters };
      const url = `/reports/v2/export/${tab}.${format}`;
      const res = await api.get(url, { params, responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `laporan-${tab}.${format}`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(a.href);
      a.remove();
      toast.success(`Laporan ${tab} berhasil diunduh`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Gagal mengunduh ${format.toUpperCase()}`);
    }
  };
  return (
    <div className="inline-flex items-center gap-2">
      <Button
        size="sm" variant="secondary" disabled={disabled}
        onClick={() => handleDownload('xlsx')}
        data-testid={`report-export-xlsx-${tab}`}
      >
        <FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" /> Export Excel
      </Button>
      {hasPdf && (
        <Button
          size="sm" variant="secondary" disabled={disabled}
          onClick={() => handleDownload('pdf')}
          data-testid={`report-export-pdf-${tab}`}
        >
          <FileDown className="h-3.5 w-3.5 mr-1.5" /> Export PDF
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------
// Generic table primitive (with mobile card fallback)
// ---------------------------------------------------------------
export function ReportTable({ columns, rows, mobileCard, emptyText, getRowKey, onRowClick }) {
  if (!rows || rows.length === 0) {
    return <ReportEmpty>{emptyText || 'Belum ada data untuk periode ini.'}</ReportEmpty>;
  }
  return (
    <>
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-2 py-2 text-[11px] font-medium uppercase tracking-wide text-slate-300 text-${c.align || 'left'}`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr
                key={getRowKey ? getRowKey(r, idx) : (r.id || idx)}
                className={`border-b border-border/40 hover:bg-white/[0.03] ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`px-2 py-2 text-${c.align || 'left'} ${c.cellClassName || 'text-slate-200'}`}
                  >
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="md:hidden space-y-2 p-2">
        {rows.map((r, idx) => (
          <div
            key={getRowKey ? getRowKey(r, idx) : (r.id || idx)}
            className="rounded-lg border border-border bg-card/60 p-3"
            onClick={onRowClick ? () => onRowClick(r) : undefined}
          >
            {mobileCard ? mobileCard(r) : (
              <div className="text-sm text-slate-200">{JSON.stringify(r).slice(0, 80)}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}

export const fIDR = (v) => `Rp ${Math.round(Number(v || 0)).toLocaleString('id-ID')}`;
export const fIDRCompact = (v) => {
  const n = Number(v || 0);
  if (Math.abs(n) >= 1_000_000_000) return `Rp ${(n / 1_000_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000_000) return `Rp ${(n / 1_000_000).toFixed(1)}Jt`;
  if (Math.abs(n) >= 1_000) return `Rp ${(n / 1_000).toFixed(0)}rb`;
  return `Rp ${n.toLocaleString('id-ID')}`;
};
export const fDateID = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
